import { FC, Fragment, ReactNode, useState } from "react";
import { TableHeader } from "./TableHeader";
import classNames from "classnames";
import { isPlainObject } from "utils/funcs";

export interface IColumn {
  title: string;
  render?: any;
  key?: string;
  dbColName?: string;
  show?: boolean;
  freezeIndex?: any;
  color?: any;
  freeze?: boolean;
  select?: boolean;
}

export interface ITable {
  rowSelection?: boolean;
  data: any;
  totalItems: number;
  exporting?: boolean;
  setPagination?: any;
  isSort?: boolean;
  setLoading?: any;
  columnFilters?: any;
  onSort?: (type: string) => void;
  pagination?: any;
  filters?: any;
  exportData?: any;
  columns: any;
  exportLabel?: string;
  showSearch?: boolean;
  setData?: any;
  exportButton?: boolean;
  showPagination?: boolean;
  handleExport?: any;
  loadingData?: boolean;
  expandable?: {
    rowKey: (record: any) => string;
    expandedRowRender: (record: any) => React.ReactNode;
  };
  expandedRowKeys?: string[];
  onExpandedRowsChange?: (keys: string[]) => void;
  /** Persist search box to sessionStorage (survives detail navigation). */
  persistKey?: string;
  searchText?: string;
  onSearchTextChange?: (value: string) => void;
}

export interface ITableBody extends ITable {
  showingData: any;
  itemsPerPage: number;
  pagination?: any;
  setPagination?: any;
  currentPage: number;
}

function generateNumberArray(N: number) {
  const numberArray: number[] = [];
  for (let i = 10; i <= N; i += 10) {
    numberArray.push(i);
  }
  return numberArray;
}

/** Plain numbers / currency / percentages read best right-aligned. */
const looksNumeric = (v: unknown) => {
  if (typeof v === "number") return true;
  if (typeof v !== "string") return false;
  const s = v.trim();
  return s !== "" && /^[-+$]?[\d,]*\.?\d+%?$/.test(s);
};

export const Table: FC<ITable> = ({
  columns,
  loadingData,
  pagination,
  setPagination,
  data,
  totalItems,
  showPagination = true,
  onSort,
  exportLabel = "Export CSV",
  columnFilters,
  showSearch = true,
  filters,
  rowSelection,
  handleExport,
  setData,
  exportButton = true,
  exporting,
  expandable,
  expandedRowKeys = [],
  onExpandedRowsChange,
  persistKey,
  searchText: controlledSearch,
  onSearchTextChange,
}) => {
  const rows = Array.isArray(data) ? data : [];

  if (process.env.NODE_ENV !== "production" && !Array.isArray(data)) {
    console.warn("Table expected `data` to be an array but received:", data);
  }

  const readPersistedSearch = () => {
    if (!persistKey) return "";
    try {
      return sessionStorage.getItem(`table-search:${persistKey}`) ?? "";
    } catch {
      return "";
    }
  };

  const [perPage, setPerPage] = useState(
    +(pagination?.pageSize ?? pagination?.limit ?? 50)
  );
  const [currentPage, setCurrentPage] = useState(
    +(pagination?.pageNumber ?? pagination?.page ?? 1)
  );

  const syncServerPagination = (nextPage: number, nextPerPage: number) => {
    setCurrentPage(nextPage);
    setPerPage(nextPerPage);
    if (setPagination && pagination) {
      setPagination({
        ...pagination,
        page: nextPage,
        pageNumber: nextPage,
        limit: nextPerPage,
        pageSize: nextPerPage,
      });
    }
  };
  const [internalSearch, setInternalSearch] = useState(readPersistedSearch);

  const searchText =
    controlledSearch !== undefined ? controlledSearch : internalSearch;

  const setSearchText = (value: string) => {
    if (onSearchTextChange) onSearchTextChange(value);
    else setInternalSearch(value);
    if (persistKey) {
      try {
        sessionStorage.setItem(`table-search:${persistKey}`, value);
      } catch {
        /* ignore */
      }
    }
  };

  const q = searchText.trim().toLowerCase();
  const filteredData = rows.filter(
    (item: { [x: string]: { toString: () => string } }) => {
      if (!q) return true;
      return Object.keys(item).some((key) =>
        item[key]?.toString()?.toLowerCase()?.includes(q)
      );
    }
  );

  const pageNumber: number[] = [];
  for (let i = 1; i <= Math.ceil(totalItems / perPage); i++) {
    pageNumber.push(i);
  }

  const unwrapCellValue = (v: unknown): React.ReactNode => {
    if (v == null) return "";
    if (typeof v === "string" || typeof v === "number") return v;
    if (typeof v === "boolean") return v ? "Yes" : "No";
    if (typeof v === "bigint") return v.toString();
    if (v instanceof Date) return v.toISOString().split("T")[0];
    if (isPlainObject(v)) {
      const o = v;
      if ("value" in o) return unwrapCellValue(o.value);
      if (o.$numberInt != null) return String(o.$numberInt);
      if (o.$numberLong != null) return String(o.$numberLong);
      if (o.$numberDouble != null) return String(o.$numberDouble);
      if (o.$date != null) {
        const d = o.$date;
        if (typeof d === "string") return d.split("T")[0];
        if (d instanceof Date) return d.toISOString().split("T")[0];
      }
      const toStr = (v as { toString?: () => string }).toString;
      if (typeof toStr === "function") {
        const s = toStr.call(v);
        if (s && !s.startsWith("[object ")) return s;
      }
      try {
        return JSON.stringify(v);
      } catch {
        return "";
      }
    }
    return String(v);
  };

  /** Volition/API rows often omit nested objects; never crash the whole table. */
  const safeRender = (
    renderFn: (...args: unknown[]) => unknown,
    ...args: unknown[]
  ): ReactNode => {
    try {
      const out = renderFn(...args);
      return out ?? "";
    } catch {
      return "";
    }
  };

  const renderTableColumnData = (col: IColumn, data: any, index: number) => {
    const column =
      col.dbColName != null && data != null
        ? data[col.dbColName as keyof typeof data]
        : undefined;

    const baseCell = classNames(
      "px-4 py-2.5 text-[13px] whitespace-nowrap font-normal bg-white group-hover:bg-surface transition-colors",
      {
        sticky: col.freeze,
        [col.freezeIndex]: col.freeze,
        "bg-opacity-100": col?.freeze,
      }
    );

    if (col.hasOwnProperty("render") && !col.hasOwnProperty("dbColName")) {
      return (
        <td key={col.key} className={classNames(baseCell, "text-ink")}>
          {safeRender(col.render, data)}
        </td>
      );
    } else if (
      col.hasOwnProperty("render") &&
      col.hasOwnProperty("dbColName")
    ) {
      return (
        <td
          key={col.key}
          onClick={() => {
            try {
              col.render(data);
            } catch {
              /* ignore bad row data */
            }
          }}
          className={classNames(baseCell, "text-ink-secondary cursor-pointer")}
        >
          {safeRender(col.render, column, data?.ID as string)}
        </td>
      );
    } else {
      const value = unwrapCellValue(column);
      return (
        <td
          key={col.key}
          className={classNames(baseCell, "text-ink-secondary", {
            "tabular-nums": looksNumeric(column),
          })}
        >
          {value}
        </td>
      );
    }
  };

  const startIndex = (currentPage - 1) * perPage;

  return (
    <Fragment>
      {/* ── Toolbar ─────────────────────────────────────────────── */}
      <div className="bg-white rounded-t-xl border border-b-0 border-line px-4 pt-3.5 pb-3">
        {loadingData && (
          <p className="text-xs text-center text-ink-muted pb-2 animate-pulse-soft">
            Loading data, please wait…
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2 [&_input]:h-9 [&_select]:h-9 [&_select]:text-sm [&_input]:text-sm [&_input:not([type=search])]:min-w-[150px] [&_select]:min-w-[130px]">
          {/* Search */}
          {showSearch && (
            <div className="relative flex-1 min-w-[200px] max-w-md xl:max-w-xl 2xl:max-w-none">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-4 h-4 absolute text-ink-muted left-3 top-1/2 -translate-y-1/2 pointer-events-none"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                />
              </svg>
              <input
                type="search"
                placeholder="Search in results…"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full pl-9 pr-3 text-sm text-ink bg-surface border-line rounded-lg focus:bg-white"
              />
            </div>
          )}

          {/* Extra filters */}
          {filters}

          {/* Rows selector lives in the footer; show it here when there is no footer */}
          {!showPagination && (
            <div className="flex items-center gap-1.5">
              <label className="text-xs text-ink-muted whitespace-nowrap">Rows</label>
              <select
                onChange={(e) => {
                  syncServerPagination(1, +e.target.value);
                }}
                value={perPage}
                className="text-xs rounded-lg pl-2.5 pr-7 bg-white text-ink"
              >
                {generateNumberArray(1000).map((item) => (
                  <option key={item} defaultValue={perPage} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Export button */}
          {exportButton && (
            <button
              onClick={handleExport}
              disabled={exporting}
              className="btn-secondary ml-auto h-9"
            >
              {exporting ? (
                <>
                  <svg
                    className="animate-spin w-3.5 h-3.5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  <span>Exporting…</span>
                </>
              ) : (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.75}
                    stroke="currentColor"
                    className="w-3.5 h-3.5 text-ink-muted"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
                    />
                  </svg>
                  <span>{exportLabel}</span>
                </>
              )}
            </button>
          )}
        </div>
        {columnFilters && (
          <div className="mt-3 pt-3 border-t border-line">
            <div className="flex items-center gap-1.5 mb-1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.75}
                stroke="currentColor"
                className="w-3.5 h-3.5 text-ink-muted"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 01-.659 1.591l-5.432 5.432a2.25 2.25 0 00-.659 1.591v2.927a2.25 2.25 0 01-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 00-.659-1.591L3.659 7.409A2.25 2.25 0 013 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0112 3z"
                />
              </svg>
              <span className="section-title">Filters</span>
            </div>
            <div className="[&_input]:h-9 [&_select]:h-9 [&_input]:text-sm [&_select]:text-sm [&_label]:mb-0">
              {columnFilters}
            </div>
          </div>
        )}
      </div>

      {/* ── Table ───────────────────────────────────────────────── */}
      <div className="relative isolate w-full max-h-screen min-h-[12rem] overflow-x-auto bg-white border border-line">
        <table className="min-w-full w-full">
          <TableHeader
            rowSelection={rowSelection}
            columns={columns}
            setData={setData}
            totalItems={totalItems}
            onSort={onSort}
            data={filteredData}
            expandable={expandable}
          />
          <tbody
            className={classNames("divide-y divide-line", {
              "h-60": !rows.length && !loadingData,
            })}
          >
            {/* Empty state */}
            {!loadingData && (filteredData?.length === 0 || !filteredData) && (
              <tr>
                <td
                  colSpan={
                    columns.filter((c: IColumn) => c?.show === undefined || c?.show)
                      .length + (expandable ? 2 : 1) + (rowSelection ? 1 : 0)
                  }
                  className="py-14 text-center sticky left-0"
                >
                  <div className="mx-auto w-10 h-10 rounded-full bg-surface-muted flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-ink-muted"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                      />
                    </svg>
                  </div>
                  <p className="mt-3 text-sm font-medium text-ink-secondary">No data found</p>
                  <p className="mt-0.5 text-xs text-ink-muted">Try changing the filters or search.</p>
                </td>
              </tr>
            )}

            {/* Data rows */}
            {filteredData?.length !== 0 &&
              filteredData?.map((d: any, index: number) => {
                const rowKey = expandable?.rowKey(d);
                const isExpanded =
                  rowKey != null && expandedRowKeys?.includes(rowKey);
                const toggleExpand = () => {
                  if (!onExpandedRowsChange || rowKey == null) return;
                  if (isExpanded) {
                    onExpandedRowsChange(
                      expandedRowKeys.filter((k) => k !== rowKey)
                    );
                  } else {
                    onExpandedRowsChange([...expandedRowKeys, rowKey]);
                  }
                };
                return (
                  <Fragment key={expandable?.rowKey(d) ?? index}>
                    <tr className="group bg-white">
                      {expandable && (
                        <td
                          className="w-10 px-2 py-2.5 text-sm cursor-pointer select-none bg-white group-hover:bg-surface transition-colors"
                          onClick={toggleExpand}
                        >
                          <span className="inline-flex text-ink-muted hover:text-primary transition-colors">
                            {isExpanded ? (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-4 h-4"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="m19.5 8.25-7.5 7.5-7.5-7.5"
                                />
                              </svg>
                            ) : (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-4 h-4"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="m8.25 4.5 7.5 7.5-7.5 7.5"
                                />
                              </svg>
                            )}
                          </span>
                        </td>
                      )}
                      <td className="px-4 py-2.5 text-xs text-ink-muted tabular-nums bg-white group-hover:bg-surface transition-colors">
                        {pagination && pagination?.page > 1
                          ? pagination?.limit * pagination?.page +
                            index +
                            1 -
                            10
                          : startIndex + index + 1}
                      </td>
                      {columns.map((col) => {
                        if (col?.show === undefined || col?.show) {
                          return renderTableColumnData(col, d, index);
                        }
                        return null;
                      })}
                    </tr>
                    {expandable && isExpanded && (
                      <tr className="bg-surface">
                        <td
                          colSpan={
                            (columns.filter(
                              (c: IColumn) =>
                                c?.show === undefined || c?.show
                            ).length ?? columns.length) +
                            2
                          }
                          className="px-4 py-3 align-top border-b border-line"
                        >
                          {expandable.expandedRowRender(d)}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
          </tbody>
        </table>
        {loadingData && (
          <div
            className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/70 backdrop-blur-[1px]"
            aria-busy="true"
            aria-live="polite"
          >
            <svg
              aria-hidden="true"
              className="w-7 h-7 text-line animate-spin fill-primary"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
            <p className="mt-2 text-xs font-medium text-ink-muted">Loading…</p>
          </div>
        )}
      </div>

      {/* ── Pagination ──────────────────────────────────────────── */}
      {showPagination && (
        <div className="bg-white border border-t-0 border-line rounded-b-xl">
          <div className="flex items-center justify-between px-4 py-2.5">
            {/* Mobile prev/next */}
            <div className="flex justify-between flex-1 sm:hidden gap-2">
              <button
                onClick={() => syncServerPagination(currentPage - 1, perPage)}
                disabled={currentPage === pageNumber[0]}
                className="btn-secondary btn-sm"
              >
                Previous
              </button>
              <button
                onClick={() => syncServerPagination(currentPage + 1, perPage)}
                disabled={currentPage === pageNumber.length}
                className="btn-secondary btn-sm"
              >
                Next
              </button>
            </div>

            {/* Desktop pagination */}
            <div className="hidden sm:flex flex-1 items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <label className="text-xs text-ink-muted whitespace-nowrap">Rows</label>
                  <select
                    onChange={(e) => {
                      syncServerPagination(1, +e.target.value);
                    }}
                    value={perPage}
                    className="text-xs rounded-lg pl-2.5 pr-7 py-1 bg-white text-ink"
                  >
                    {generateNumberArray(1000).map((item) => (
                      <option key={item} defaultValue={perPage} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                </div>
                <span className="h-4 w-px bg-line" />
                <p className="text-xs text-ink-muted tabular-nums">
                  Page{" "}
                  <span className="font-medium text-ink">
                    {pagination ? pagination.pageNumber : currentPage}
                  </span>{" "}
                  of{" "}
                  <span className="font-medium text-ink">{pageNumber.length}</span>
                  {totalItems > 0 && (
                    <>
                      {" "}· <span className="font-medium text-ink">{totalItems}</span> total
                    </>
                  )}
                </p>
                <select
                  onChange={(eve) => {
                    syncServerPagination(+eve.target.value, perPage);
                  }}
                  className="pl-2.5 pr-7 py-1 rounded-lg text-xs text-ink-secondary"
                >
                  <option disabled>Go to page</option>
                  {pageNumber.map((number) => (
                    <option key={number} value={number}>
                      {number}
                    </option>
                  ))}
                </select>
                {loadingData && (
                  <p className="text-xs text-ink-muted animate-pulse-soft">
                    Loading…
                  </p>
                )}
              </div>
              <nav className="inline-flex items-center gap-1.5" aria-label="Pagination">
                <button
                  onClick={() => syncServerPagination(currentPage - 1, perPage)}
                  disabled={currentPage === pageNumber[0]}
                  className="btn-secondary btn-sm"
                >
                  ← Previous
                </button>
                <button
                  onClick={() => syncServerPagination(currentPage + 1, perPage)}
                  disabled={
                    currentPage === pageNumber.length ||
                    !rows.length ||
                    !filteredData.length
                  }
                  className="btn-secondary btn-sm"
                >
                  Next →
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
};
